import type { Agent, Persisted } from '@tunasong/schemas'
import { getIconByKey } from '@tunasong/icons'
import type { BlockAffordance } from './block-affordance.js'
import { resolveSectionAtCursor as defaultResolveSectionAtCursor } from './lib/resolve-section-at-cursor.js'
import type { ResolvedSectionBlock } from './lib/resolve-section-at-cursor.js'
import type { AgentDefinition, Command, CommandFn, TunaEditor } from './plugin-types.js'

/**
 * Cmd params for an affordance-derived command. Tests invoke `cmd` with
 * this shape; production callers (palette / block-action menus) hand in
 * the same fields when invoking. The structural type sits next to the
 * resolved `Command` because the palette tooling carries a generic
 * `TParams = any` and the typing is enforced at the call sites that
 * build these args, not by `Command.cmd` itself.
 */
interface BlockAffordanceCommandParams {
  editor: TunaEditor
  dispatch: (action: unknown) => unknown
}

/**
 * Module-level injected dependencies, populated at app bootstrap. Tests
 * pass overrides per call; production callers omit them, and the
 * injected production thunk is used.
 */
export interface BlockAffordanceCommandDeps {
  resolveSectionAtCursor?: (editor: TunaEditor) => ResolvedSectionBlock | null
  /**
   * The thunk dispatched when an affordance-derived command fires.
   * Injected at bootstrap from @tunasong/plugin-agent/integration to
   * keep plugin-lib independent of plugin-agent.
   */
  openSidebarAgentWithFocus?: (args: { agentId: string; focus: unknown; draft?: string }) => unknown
}

let injectedDeps: BlockAffordanceCommandDeps = {}

/**
 * Called from app bootstrap (apps/tunasong) to inject the production
 * thunk reference. Without this call, affordance-derived commands are
 * no-ops in production. Tests bypass this by passing `deps` per call.
 */
export const setBlockAffordanceCommandDeps = (deps: BlockAffordanceCommandDeps): void => {
  injectedDeps = deps
}

/**
 * Convert one (AgentDefinition, attached Agent, BlockAffordance) into
 * a palette Command. Name follows `<Agent Name>: <affordance label>`.
 * `cmd` resolves the cursor's enclosing section at invocation time and
 * returns `null` when no match (the palette's disabled-when-not-applicable
 * convention).
 */
export const blockAffordanceToCommand = (
  definition: AgentDefinition,
  agent: Persisted<Agent>,
  affordance: BlockAffordance,
  deps: BlockAffordanceCommandDeps = {}
): Command => {
  // Typed as a plain function returning `null | undefined` so the
  // "not applicable" branches stay explicit. Cast once at the property
  // assignment below because `CommandFn` is `(params) => void | TunaEditor
  // | Promise<...>` — the palette runner already tolerates a null/undefined
  // return at runtime (sibling pattern: tunabrain-next-line sets `cmd: null`
  // when not applicable, but that decision is made at command-build time;
  // here the cursor state is only known when the user invokes the action).
  const run = (params: BlockAffordanceCommandParams): null | undefined => {
    const rootId = params.editor.rootElement?.id
    if (typeof rootId !== 'string' || rootId.length === 0) {
      return null
    }
    const resolver = deps.resolveSectionAtCursor ?? injectedDeps.resolveSectionAtCursor ?? defaultResolveSectionAtCursor
    const block = resolver(params.editor)
    if (!block) {
      return null
    }
    if (affordance.blockTypes !== '*' && !affordance.blockTypes.includes(block.type)) {
      return null
    }
    const blockRef = {
      id: block.id,
      type: block.type,
      entityId: rootId,
      summary: block.summary,
    }
    const { messageTemplate, focus } = affordance.buildAsk(blockRef)
    const open = deps.openSidebarAgentWithFocus ?? injectedDeps.openSidebarAgentWithFocus
    if (open) {
      params.dispatch(open({ agentId: agent.id, focus, draft: messageTemplate }))
    }
    return undefined
  }
  return {
    id: `${agent.id}/${affordance.id}`,
    name: `${definition.name}: ${affordance.label}`,
    Icon: getIconByKey(affordance.iconKey) ?? getIconByKey(definition.iconKey),
    blockOnRun: false,
    cmd: run as unknown as CommandFn,
  }
}
